import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { DEFAULT_LANGUAGE_CODE } from '@mews-commander/constants';
import { primitiveTypeUniq } from '@mews/fp';
import { getLanguageOptions } from '@mews-commander/legacy-entities/src/ducks/languages';
import { useCommanderSelector } from '@mews-commander/utils';
import { useEnterprisesProductContext } from '../context/EnterprisesProductContext';
import { DetailsFieldName } from '../components/forms/schemas/configureProductFormSchema';
import { Option, sortOptions, useOptionTabs } from './useOptionTabs';

export const useProductLanguages = () => {
    const {
        state: { enterprises },
    } = useEnterprisesProductContext();

    const enterprisesDefaultLanguageCodes = useMemo(
        () => enterprises.map(({ defaultLanguageCode }) => defaultLanguageCode),
        [enterprises]
    );

    const { defaultLanguageCode: portfolioDefaultLanguageCode } = useCurrentEnterprise();

    const languageOptions = useCommanderSelector(getLanguageOptions);

    const { watch } = useFormContext();

    const languagesList: Option[] = useMemo(
        () =>
            languageOptions.map((lang) => {
                const isDefaultLanguageCode = portfolioDefaultLanguageCode === lang.value;

                const defaults = primitiveTypeUniq([
                    portfolioDefaultLanguageCode,
                    ...enterprisesDefaultLanguageCodes,
                ]).includes(lang.value || DEFAULT_LANGUAGE_CODE);

                return {
                    ...lang,
                    value: lang.value || DEFAULT_LANGUAGE_CODE,
                    isDefault: isDefaultLanguageCode,
                    disabled: defaults,
                };
            }),
        [languageOptions, portfolioDefaultLanguageCode, enterprisesDefaultLanguageCodes]
    );

    const { activeTab, selectedOptions, setActiveTab, setSelectedOptions, toggleSelection } = useOptionTabs({
        optionsList: languagesList,
        fieldPathKey: DetailsFieldName.Languages,
    });

    useEffect(() => {
        const currentLanguageValues = Object.keys(watch(DetailsFieldName.Languages) || {});

        const { defaultEnterpriseLanguage, selectedLanguages } = languagesList.reduce<{
            defaultEnterpriseLanguage: Option[];
            selectedLanguages: Option[];
        }>(
            (acc, language) => {
                if (language.isDefault || language.disabled) {
                    acc.defaultEnterpriseLanguage.push(language);
                } else if (currentLanguageValues.includes(language.value)) {
                    acc.selectedLanguages.push(language);
                }
                return acc;
            },
            { defaultEnterpriseLanguage: [], selectedLanguages: [] }
        );

        const sortedSelectedOptions = sortOptions([...defaultEnterpriseLanguage, ...selectedLanguages]);
        setActiveTab(portfolioDefaultLanguageCode || DEFAULT_LANGUAGE_CODE);
        setSelectedOptions(sortedSelectedOptions);
    }, [portfolioDefaultLanguageCode, languagesList, setActiveTab, setSelectedOptions, watch]);

    return { activeTab, setActiveTab, portfolioDefaultLanguageCode, languagesList, toggleSelection, selectedOptions };
};
